import { FC, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Divider } from '@mui/material';
import { getAssertions } from 'entities/Assertions/api';
import { AssertionsType } from 'entities/Assertions/types';
import { Loader } from 'shared/ui/Loader';
import { PageHeader } from 'shared/ui/PageHeader';
import { AsTable } from 'widgets/Extensions/Details/ui/AssertionSets/ui/AsTable/ui/AssertionsTable';
import {
	EditAssertionModal,
	CreateAssertionModal,
} from 'widgets/Extensions/Details/ui/AssertionSets/ui/AsModals/AssertionsEditor';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { Button } from 'shared/ui/Button';
import { ColorPalette } from 'shared/consts/colorPalette';

export const AssertionsListWidget: FC<{ assetionId: string }> = ({ assetionId }) => {
	const { t } = useTranslation();
	const [createIsOpen, setCreateIsOpen] = useState(false);
	const [assertionToEdit, setAssertionToEdit] = useState<AssertionsType | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: ['get/assertion-sets/assertions'],
		queryFn: () => getAssertions({ id: assetionId }),
		onError: (err) => {
			const errorMessage = getErrorMessage(err);
			enqueueSnackbar({
				message: errorMessage,
				variant: 'error',
			});
		},
	});

	const handleCloseCreate = () => setCreateIsOpen(false);
	const handleCloseEdit = () => {
		setAssertionToEdit(null);
	};

	const handleOpenCreate = () => setCreateIsOpen(true);
	const handleOpenEdit = (assertion: AssertionsType) => {
		setAssertionToEdit(assertion);
	};

	return (
		<>
			<PageHeader
				title={t('assertions.as_title')}
				description={t('assertions.as_description')}
				backBtn
			/>
			{assetionId && (
				<CreateAssertionModal id={assetionId} open={createIsOpen} handleClose={handleCloseCreate} />
			)}
			{assertionToEdit && (
				<EditAssertionModal
					open={!!assertionToEdit}
					handleClose={handleCloseEdit}
					assertionToEdit={assertionToEdit}
				/>
			)}
			{isLoading ? (
				<Loader />
			) : (
				<>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							justifyContent: 'space-between',
							marginBottom: '34px',
						}}
					>
						<Typography variant={'h5'} color={ColorPalette.astronaut}>
							{data?.data.assertionSet.name}
						</Typography>
						<Button onClick={handleOpenCreate}>{t('assertions.add_as')}</Button>
					</Box>
					<AsTable assertions={data?.data.assertions} handleOpenEdit={handleOpenEdit} />
				</>
			)}
		</>
	);
};
